import { z } from 'zod';

// One typed-field system for every building block: custom data (metafields,
// metaobject fields), block and theme-section settings, and automation action
// inputs. A value that validates here validates everywhere: in the owner's
// panel, in studio, in the Plugin API and in the site runtime.

export const FIELD_TYPES = {
  single_line_text: 'One line of text',
  multi_line_text: 'Several lines of plain text',
  rich_text: 'Formatted text (a small, safe subset of markdown)',
  integer: 'A whole number',
  decimal: 'A number with decimals',
  boolean: 'True or false',
  date: 'A date (YYYY-MM-DD)',
  date_time: 'A date and time (ISO 8601)',
  url: 'An https or relative link',
  email: 'An email address',
  color: 'A hex color (#RRGGBB)',
  money: 'An amount in minor units plus a currency',
  image: 'An image URL with optional alt text',
  select: 'One value from a fixed list',
  json: 'Structured data (max 16 KB)',
  product_reference: 'A product in this shop',
  variant_reference: 'A product variant in this shop',
  collection_reference: 'A collection in this shop',
  metaobject_reference: 'An entry of a metaobject type',
  page_reference: 'A page path on this site',
} as const;

export type FieldType = keyof typeof FIELD_TYPES;

export const FIELD_TYPE_IDS = Object.keys(FIELD_TYPES) as FieldType[];

const handle = z.string().regex(/^[a-z][a-z0-9_]{0,40}$/, 'lowercase letters, digits and _; starts with a letter');

/** Optional constraints, checked by validateFieldValue. Which apply depends on the type. */
export const fieldValidationsSchema = z
  .object({
    min: z.number().optional(),
    max: z.number().optional(),
    /** Text length bounds. */
    minLength: z.number().int().min(0).optional(),
    maxLength: z.number().int().min(1).max(100_000).optional(),
    // No regex patterns: a third-party regex would run in studio (ReDoS).
    /** For metaobject_reference: the metaobject type handle it points to. */
    metaobjectType: z.string().max(81).optional(),
  })
  .strict();

export type FieldValidations = z.infer<typeof fieldValidationsSchema>;

export const fieldDefinitionSchema = z.object({
  key: handle,
  label: z.string().min(1).max(80),
  type: z.enum(FIELD_TYPE_IDS as [FieldType, ...FieldType[]]),
  /** A list of values of `type` (max 50 items). */
  list: z.boolean().optional(),
  help: z.string().max(300).optional(),
  required: z.boolean().optional(),
  /** For `select`. */
  options: z
    .array(z.object({ value: z.string().max(80), label: z.string().max(80) }))
    .min(1)
    .max(50)
    .optional(),
  validations: fieldValidationsSchema.optional(),
  /** Default value (block settings, metaobject fields). Must itself validate. */
  default: z.unknown().optional(),
});

export type FieldDefinition = z.infer<typeof fieldDefinitionSchema>;
export type FieldDefinitionInput = z.input<typeof fieldDefinitionSchema>;

export const MAX_LIST_ITEMS = 50;
export const MAX_JSON_BYTES = 16_384;

export interface MoneyValue {
  amount: number;
  currency: string;
}

export interface ImageValue {
  url: string;
  alt?: string;
}

const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isLink(v: string): boolean {
  if (v.startsWith('/') && !v.startsWith('//')) {
    return true;
  }

  try {
    return new URL(v).protocol === 'https:';
  } catch {
    return false;
  }
}

function checkOne(def: FieldDefinition, v: unknown): string | null {
  const val = def.validations;
  const text = (s: unknown): string | null => {
    if (typeof s !== 'string') {
      return 'must be text';
    }

    if (val?.minLength !== undefined && s.length < val.minLength) {
      return `must be at least ${val.minLength} characters`;
    }

    if (s.length > (val?.maxLength ?? (def.type === 'single_line_text' ? 500 : 20_000))) {
      return 'is too long';
    }

    return null;
  };
  const num = (n: unknown, integer: boolean): string | null => {
    if (typeof n !== 'number' || !Number.isFinite(n) || (integer && !Number.isInteger(n))) {
      return integer ? 'must be a whole number' : 'must be a number';
    }

    if (val?.min !== undefined && n < val.min) {
      return `must be at least ${val.min}`;
    }

    if (val?.max !== undefined && n > val.max) {
      return `must be at most ${val.max}`;
    }

    return null;
  };

  switch (def.type) {
    case 'single_line_text':
      return typeof v === 'string' && /[\r\n]/.test(v) ? 'must be one line' : text(v);
    case 'multi_line_text':
    case 'rich_text':
      return text(v);
    case 'integer':
      return num(v, true);
    case 'decimal':
      return num(v, false);
    case 'boolean':
      return typeof v === 'boolean' ? null : 'must be true or false';
    case 'date':
      return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v))
        ? null
        : 'must be a date (YYYY-MM-DD)';
    case 'date_time':
      return typeof v === 'string' && !Number.isNaN(Date.parse(v)) && /T/.test(v) ? null : 'must be an ISO date-time';
    case 'url':
      return typeof v === 'string' && v.length <= 2_000 && isLink(v) ? null : 'must be an https or relative link';
    case 'email':
      return typeof v === 'string' && v.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
        ? null
        : 'must be an email address';
    case 'color':
      return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v) ? null : 'must be a hex color like #1A2B3C';
    case 'money': {
      const m = v as MoneyValue;

      return m && typeof m === 'object' && Number.isInteger(m.amount) && /^[A-Z]{3}$/.test(String(m.currency))
        ? num(m.amount, true)
        : 'must be { amount (minor units), currency }';
    }
    case 'image': {
      const i = v as ImageValue;

      return i && typeof i === 'object' && typeof i.url === 'string' && isLink(i.url) && (i.alt ?? '').length <= 300
        ? null
        : 'must be { url, alt? } with an https url';
    }
    case 'select':
      return typeof v === 'string' && (def.options ?? []).some((o) => o.value === v)
        ? null
        : 'is not one of the options';
    case 'json':
      try {
        return JSON.stringify(v ?? null).length <= MAX_JSON_BYTES ? null : 'is larger than 16 KB';
      } catch {
        return 'is not JSON';
      }
    case 'product_reference':
    case 'variant_reference':
    case 'collection_reference':
    case 'metaobject_reference':
      return typeof v === 'string' && uuidRe.test(v) ? null : 'must be an id';
    case 'page_reference':
      return typeof v === 'string' && /^\/[a-zA-Z0-9\-._~/]*$/.test(v) && v.length <= 300
        ? null
        : 'must be a page path like /about';
  }

  // A type from a newer contract this version doesn't know: reject rather than accept unchecked.
  return 'has an unknown field type';
}

/**
 * Validate a value against a field definition. Returns null when valid,
 * otherwise a sentence fragment ("must be a number"). `undefined`/`null` is
 * valid unless the field is required.
 */
export function validateFieldValue(def: FieldDefinition, value: unknown): string | null {
  if (value === undefined || value === null || value === '') {
    return def.required ? 'is required' : null;
  }

  if (def.list) {
    if (!Array.isArray(value)) {
      return 'must be a list';
    }

    if (value.length > MAX_LIST_ITEMS) {
      return `can have at most ${MAX_LIST_ITEMS} items`;
    }

    for (const [i, item] of value.entries()) {
      const err = checkOne(def, item);

      if (err) {
        return `item ${i + 1} ${err}`;
      }
    }

    return null;
  }

  return checkOne(def, value);
}

/** Validate a record of values against field definitions. Unknown keys are errors. */
export function validateFields(
  defs: readonly FieldDefinition[],
  values: Record<string, unknown>,
): { ok: true } | { ok: false; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const known = new Set(defs.map((d) => d.key));

  for (const key of Object.keys(values ?? {})) {
    if (!known.has(key)) {
      errors[key] = 'is not a field';
    }
  }

  for (const def of defs) {
    const err = validateFieldValue(def, values?.[def.key]);

    if (err) {
      errors[def.key] = `${def.label} ${err}`;
    }
  }

  return Object.keys(errors).length ? { ok: false, errors } : { ok: true };
}

/** Field definitions with their defaults applied to `values`. */
export function withFieldDefaults(
  defs: readonly FieldDefinition[],
  values: Record<string, unknown> = {},
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...values };

  for (const def of defs) {
    if (out[def.key] === undefined && def.default !== undefined) {
      out[def.key] = def.default;
    }
  }

  return out;
}

/** Shared manifest refinement: unique keys, options for selects, defaults that validate. */
export function checkFieldDefinitions(
  defs: readonly FieldDefinition[],
  report: (message: string) => void,
  where: string,
): void {
  const keys = new Set<string>();

  for (const def of defs) {
    if (keys.has(def.key)) {
      report(`${where}: duplicate field "${def.key}"`);
    }

    keys.add(def.key);

    if (def.type === 'select' && !def.options?.length) {
      report(`${where}: select field "${def.key}" needs options`);
    }

    if (def.type === 'metaobject_reference' && !def.validations?.metaobjectType) {
      report(`${where}: field "${def.key}" needs validations.metaobjectType`);
    }

    if (def.default !== undefined) {
      const err = validateFieldValue({ ...def, required: false }, def.default);

      if (err) {
        report(`${where}: default for "${def.key}" ${err}`);
      }
    }
  }
}
